/**
 * WizardStepPermits (Step 4)
 *
 * Permit determination - show recommended permits based on selected scopes.
 * Users can accept or override recommendations.
 */

import { useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, AlertTriangle, Info, Sparkles } from 'lucide-react';
import { ConfidenceBadge, type ConfidenceLevel } from '@/components/ui/confidence-badge';
import { useAllPermitTypes, useJobScopesForTrades } from '@/hooks/useWizardData';
import type { WizardPermitsData, WizardScopesData, PermitRecommendation } from '@/types/wizard';

interface WizardStepPermitsProps {
  data: WizardPermitsData;
  onUpdate: (data: Partial<WizardPermitsData>) => void;
  scopesData: WizardScopesData;
}

export function WizardStepPermits({ data, onUpdate, scopesData }: WizardStepPermitsProps) {
  const { data: permitTypes, isLoading: permitTypesLoading } = useAllPermitTypes();
  const tradeIds = scopesData.tradeScopes.map((ts) => ts.tradeId);
  const { data: jobScopesMap, isLoading: scopesLoading } = useJobScopesForTrades(tradeIds);

  // Generate permit recommendations based on selected scopes
  const recommendations = useMemo(() => {
    if (!permitTypes || !jobScopesMap) return [];

    const recs: PermitRecommendation[] = [];
    const seenPermitIds = new Set<number>();

    scopesData.tradeScopes.forEach((tradeScope) => {
      if (!tradeScope.selectedScopes || tradeScope.selectedScopes.length === 0) return;

      const scopes = jobScopesMap.get(tradeScope.tradeId) || [];

      // Iterate over all selected scopes for this trade
      tradeScope.selectedScopes.forEach((selectedScopeRef) => {
        const selectedScope = scopes.find((s) => s.id === selectedScopeRef.scopeId);

        if (selectedScope?.typical_permit_types) {
          selectedScope.typical_permit_types.forEach((permitTypeId) => {
            if (seenPermitIds.has(permitTypeId)) return;
            seenPermitIds.add(permitTypeId);

            const permitType = permitTypes.find((pt) => pt.id === permitTypeId);
            if (permitType) {
              recs.push({
                permitTypeId: permitType.id,
                permitCode: permitType.code,
                permitName: permitType.name,
                confidence: 'high',
                reason: `Required for ${selectedScope.name}`,
                required: true,
                tradeCode: tradeScope.tradeCode,
                userAccepted: true, // Default to accepted
              });
            }
          });
        }
      });
    });

    return recs;
  }, [permitTypes, jobScopesMap, scopesData.tradeScopes]);

  // Initialize recommendations in state when they change
  useEffect(() => {
    if (recommendations.length > 0 && data.recommendations.length === 0) {
      onUpdate({
        recommendations,
        acceptedPermitIds: recommendations.filter((r) => r.userAccepted).map((r) => r.permitTypeId),
      });
    }
  }, [recommendations, data.recommendations.length, onUpdate]);

  // Handle permit acceptance toggle
  const handlePermitToggle = (permitTypeId: number, accepted: boolean) => {
    const updatedRecs = data.recommendations.map((rec) =>
      rec.permitTypeId === permitTypeId
        ? { ...rec, userAccepted: accepted, userOverridden: rec.required && !accepted }
        : rec
    );

    const acceptedIds = updatedRecs
      .filter((r) => r.userAccepted)
      .map((r) => r.permitTypeId);

    const rejectedIds = updatedRecs
      .filter((r) => !r.userAccepted && r.required)
      .map((r) => r.permitTypeId);

    onUpdate({
      recommendations: updatedRecs,
      acceptedPermitIds: acceptedIds,
      rejectedPermitIds: rejectedIds,
    });
  };

  // Accept all recommendations
  const handleAcceptAll = () => {
    const updatedRecs = data.recommendations.map((rec) => ({
      ...rec,
      userAccepted: true,
      userOverridden: false,
    }));

    onUpdate({
      recommendations: updatedRecs,
      acceptedPermitIds: updatedRecs.map((r) => r.permitTypeId),
      rejectedPermitIds: [],
    });
  };

  const isLoading = permitTypesLoading || scopesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const displayRecs = data.recommendations.length > 0 ? data.recommendations : recommendations;

  if (displayRecs.length === 0) {
    return (
      <div className="text-center py-12">
        <Info className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-lg font-medium">No Permit Recommendations</p>
        <p className="text-muted-foreground mt-2">
          Please go back and select specific work scopes to get permit recommendations.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Based on your project scope, we recommend the following permits:
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleAcceptAll}>
          Accept All
        </Button>
      </div>

      <div className="space-y-3">
        {displayRecs.map((rec) => {
          // Map wizard confidence to ConfidenceBadge levels
          const confidenceLevel: ConfidenceLevel = rec.confidence === 'high' ? 'high' : rec.confidence === 'medium' ? 'medium' : 'low';

          return (
            <Card
              key={rec.permitTypeId}
              className={`transition-all ${
                rec.userAccepted
                  ? 'border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/30'
                  : rec.userOverridden
                  ? 'border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/30'
                  : ''
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <Checkbox
                    checked={rec.userAccepted}
                    onCheckedChange={(checked) =>
                      handlePermitToggle(rec.permitTypeId, checked === true)
                    }
                    className="mt-1"
                  />

                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{rec.permitName}</span>
                      <ConfidenceBadge
                        level={confidenceLevel}
                        source={`Job scope: ${rec.reason}`}
                        showLabel={true}
                      />
                      {rec.required && (
                        <Badge variant="outline" className="text-xs">
                          Required
                        </Badge>
                      )}
                      {rec.tradeCode && (
                        <Badge variant="secondary" className="text-xs">
                          {rec.tradeCode}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      {rec.reason}
                    </p>

                    {rec.userOverridden && (
                      <p className="text-sm text-amber-600 dark:text-amber-500 mt-2 flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4" />
                        You&apos;ve declined a required permit. Verify this is correct with your AHJ.
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Summary */}
      <div className="p-4 bg-muted rounded-lg">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">
            {data.acceptedPermitIds.length} permit{data.acceptedPermitIds.length !== 1 ? 's' : ''} selected
          </p>
          {data.rejectedPermitIds.length > 0 && (
            <Badge variant="outline" className="text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-800">
              {data.rejectedPermitIds.length} declined
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}
